import { ReadPersonDto } from '@/api.types';

const MESES = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
] as const;

export { MESES };

/** Dia do mês do aniversário (UTC, alinhado à exibição de datas no sistema). */
export function diaAniversario(dataNascimento?: string | Date | null): string {
  if (!dataNascimento) return '-';
  return String(new Date(dataNascimento).getUTCDate()).padStart(2, '0');
}

/** Idade em anos completos a partir da data de nascimento (UTC). */
export function idadeAnos(dataNascimento?: string | Date | null): number | null {
  if (!dataNascimento) return null;
  const nasc = new Date(dataNascimento);
  if (Number.isNaN(nasc.getTime())) return null;

  const hoje = new Date();
  let idade = hoje.getUTCFullYear() - nasc.getUTCFullYear();
  const mes = hoje.getUTCMonth() - nasc.getUTCMonth();
  if (mes < 0 || (mes === 0 && hoje.getUTCDate() < nasc.getUTCDate())) {
    idade -= 1;
  }
  return idade >= 0 ? idade : null;
}

export function idadeAnosLabel(dataNascimento?: string | Date | null): string {
  const idade = idadeAnos(dataNascimento);
  return idade == null ? '-' : String(idade);
}

/** Perfis operacionais ativos (sem usuário). */
export function perfisAtivosLabels(pessoa: ReadPersonDto): string[] {
  const labels: string[] = [];
  if (pessoa.defensor) labels.push('Defensor');
  if (pessoa.estagiario) labels.push('Estagiário');
  if (pessoa.colaborador) labels.push('Colaborador');
  return labels;
}

export type PerfilAniversarioFiltro = 'defensor' | 'estagiario' | 'colaborador';

export type FiltrosPerfilAniversario = Record<PerfilAniversarioFiltro, boolean>;

export const FILTROS_PERFIL_ANIVERSARIO_PADRAO: FiltrosPerfilAniversario = {
  defensor: true,
  estagiario: true,
  colaborador: true,
};

export const OPCOES_FILTRO_PERFIL_ANIVERSARIO: { key: PerfilAniversarioFiltro; label: string }[] = [
  { key: 'defensor', label: 'Defensor' },
  { key: 'estagiario', label: 'Estagiário' },
  { key: 'colaborador', label: 'Colaborador' },
];

/** Mantém quem tem ao menos um dos perfis selecionados (ativos no join da API). */
export function filtrarAniversariantesPorPerfil(
  pessoas: ReadPersonDto[],
  filtros: FiltrosPerfilAniversario,
): ReadPersonDto[] {
  const algumMarcado = Object.values(filtros).some(Boolean);
  if (!algumMarcado) return [];

  return pessoas.filter((pessoa) => {
    if (filtros.defensor && pessoa.defensor) return true;
    if (filtros.estagiario && pessoa.estagiario) return true;
    if (filtros.colaborador && pessoa.colaborador) return true;
    return false;
  });
}

export function nomeArquivoAniversarios(mes: number): string {
  const nome = MESES.find((m) => m.value === mes)?.label?.toLowerCase() ?? String(mes);
  return `aniversarios_${nome}.xlsx`;
}

export type AniversarioSortKey = 'dia' | 'nome' | 'idade' | 'perfis';
export type AniversarioSortDirection = 'ASC' | 'DESC';

export type AniversarioSortConfig = {
  key: AniversarioSortKey;
  direction: AniversarioSortDirection;
};

function diaNumero(dataNascimento?: string | Date | null): number {
  if (!dataNascimento) return 0;
  const d = new Date(dataNascimento).getUTCDate();
  return Number.isNaN(d) ? 0 : d;
}

function valorOrdenacao(pessoa: ReadPersonDto, key: AniversarioSortKey): string | number {
  switch (key) {
    case 'dia':
      return diaNumero(pessoa.dataNascimento);
    case 'nome':
      return (pessoa.nome ?? '').trim().toLocaleLowerCase('pt-BR');
    case 'idade':
      return idadeAnos(pessoa.dataNascimento) ?? -1;
    case 'perfis':
      return perfisAtivosLabels(pessoa).join(', ').toLocaleLowerCase('pt-BR');
    default:
      return '';
  }
}

export function compararAniversariantes(
  a: ReadPersonDto,
  b: ReadPersonDto,
  config: AniversarioSortConfig,
): number {
  const valA = valorOrdenacao(a, config.key);
  const valB = valorOrdenacao(b, config.key);

  let result: number;
  if (typeof valA === 'number' && typeof valB === 'number') {
    result = valA - valB;
  } else {
    result = String(valA).localeCompare(String(valB), 'pt-BR');
  }

  if (result === 0 && config.key !== 'nome') {
    result = (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR');
  }

  return config.direction === 'ASC' ? result : -result;
}

export function ordenarAniversariantes(
  pessoas: ReadPersonDto[],
  config: AniversarioSortConfig,
): ReadPersonDto[] {
  return [...pessoas].sort((a, b) => compararAniversariantes(a, b, config));
}

export function proximaOrdenacao(
  atual: AniversarioSortConfig,
  key: AniversarioSortKey,
): AniversarioSortConfig {
  if (atual.key === key) {
    return { key, direction: atual.direction === 'ASC' ? 'DESC' : 'ASC' };
  }
  return { key, direction: 'ASC' };
}

/** Divide a lista ordenada em duas metades (esquerda recebe o eventual resto). */
export function partirEmDuasColunas<T>(itens: T[]): [T[], T[]] {
  if (itens.length <= 1) return [itens, []];
  const meio = Math.ceil(itens.length / 2);
  return [itens.slice(0, meio), itens.slice(meio)];
}

export function linhasExcelAniversariantes(pessoas: ReadPersonDto[]): string[][] {
  return pessoas.map((pessoa) => [
    diaAniversario(pessoa.dataNascimento),
    pessoa.nome ?? '',
    idadeAnosLabel(pessoa.dataNascimento),
    perfisAtivosLabels(pessoa).join(', ') || '-',
  ]);
}

export type StatusAniversarioMes = 'passado' | 'hoje' | 'futuro';

/** Posição do aniversário em relação à data de referência, dentro do mês selecionado. */
export function statusAniversarioNoMes(
  dataNascimento: string | Date | null | undefined,
  mesSelecionado: number,
  referencia: Date = new Date(),
): StatusAniversarioMes | null {
  const dia = diaNumero(dataNascimento);
  if (!dia) return null;

  const mesAtual = referencia.getUTCMonth() + 1;
  const diaAtual = referencia.getUTCDate();

  if (mesSelecionado < mesAtual) return 'passado';
  if (mesSelecionado > mesAtual) return 'futuro';
  if (dia < diaAtual) return 'passado';
  if (dia > diaAtual) return 'futuro';
  return 'hoje';
}

export function classeLinhaAniversario(status: StatusAniversarioMes | null): string {
  switch (status) {
    case 'passado':
      return 'aniv-linha-passado';
    case 'hoje':
      return 'aniv-linha-hoje';
    default:
      return '';
  }
}
